import { mutationOptions } from '@tanstack/react-query';
import { client } from '#queries/utils';
import type { JSONApiDataEntity } from '../../../src/types/json-api';
import { firstparty as kycVerification } from '#proto/index.qonto.kycb.api.kyc_verification';
import { persons as kycAcceptance } from '#proto/index.qonto.kycb.acceptance';
import { firstparty as registrationSms } from '#proto/index.qonto.registration.sms';
import { registerBaseURL } from '#constants/hosts';
import { kycQueries } from './queries';
import type {
  KycSetupAttributes,
  KycSetupRequest,
  SendKycSmsRequest,
  SendMembershipKycSmsRequest,
} from './types';

function sendVerificationSms() {
  return mutationOptions({
    mutationFn: async (data: SendKycSmsRequest) => {
      const requestBody = kycVerification.v3.ApiSendKycSmsRequestKyc.toJSON(data);

      const response = await client(`/v3/verification/kyc/${data.redoToken}/sms`, {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(requestBody),
      });

      return kycVerification.v3.ApiSendKycSmsResponseKyc.fromJSON(response);
    },
  });
}

function sendMembershipKycSms() {
  return mutationOptions({
    mutationFn: async (data: SendMembershipKycSmsRequest) => {
      const requestBody = kycVerification.v3.ApiSendMembershipKycSmsRequest.toJSON(data);

      const response = await client('/v3/verification/kyc/send_sms', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      return kycVerification.v3.ApiSendMembershipKycSmsResponse.fromJSON(response);
    },
  });
}

function createProcess() {
  return mutationOptions({
    mutationFn: async () => {
      const response = await client('/kycb-acceptance/v1/processes', {
        method: 'POST',
      });
      return kycAcceptance.firstparty.v1.ApiCreateProcessResponse.fromJSON(response);
    },
    onSuccess: (_data, _variables, _result, context) => {
      void context.client.invalidateQueries({ queryKey: kycQueries.item().queryKey });
    },
  });
}

function kycSetup() {
  return mutationOptions({
    mutationFn: async (data: KycSetupRequest) => {
      const requestBody = kycVerification.v3.ApiSetupKycRequest.toJSON(data);

      const response = await client<{ data: JSONApiDataEntity<KycSetupAttributes> }>(
        '/v3/verification/kyc/setup',
        {
          method: 'POST',
          body: JSON.stringify({
            data: {
              type: 'kyc-setup',
              attributes: requestBody,
            },
          }),
        }
      );

      return kycVerification.v3.ApiSetupKycResponse.fromJSON(response.data.attributes);
    },
  });
}

function sendRegisterSms() {
  return mutationOptions({
    mutationFn: async (data: registrationSms.v1.ApiSendSMSRequest) => {
      const requestBody = registrationSms.v1.ApiSendSMSRequest.toJSON(data);

      const response = await client(`${registerBaseURL}/v1/sms`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      return registrationSms.v1.ApiSendSMSResponse.fromJSON(response);
    },
  });
}

export const kycMutations = {
  sendVerificationSms,
  sendMembershipKycSms,
  sendRegisterSms,
  createProcess,
  kycSetup,
};
